import { useMutation } from 'react-query';
import { useStore } from '../../../context';
import { controlBackdropLoading, removeCache, repairService } from '../../../services';
import { toIsoDate, showToRoles, splitIntoChunks } from '../../../utils';
import { containersService, saleService } from '../../../services';
import { ImageObjType, RepairCreateProps, SaleCreateProps } from '../../../types';

export const useUpdateImagesMutation = () => {
  return useMutation(
    ({ id, photos = [] }: { id: string; photos: ImageObjType[] }) => {
      controlBackdropLoading(1);

      return containersService.updateImages({ id, photos }).then((res: any) => {
        let images = [];
        if (typeof res === 'object') images = res.data.data;

        return images;
      });
    },
    {
      onSettled: () => {
        removeCache(['container', 'sale']);
        controlBackdropLoading(-1);
      },
    },
  );
};

export const useUpdateDocumentsMutation = () => {
  return useMutation(
    ({ id, documents = [] }: { id: string; documents: ImageObjType[] }) => {
      controlBackdropLoading(1);

      return containersService.updateDocuments({ id, documents }).then((res: any) => {
        let docs = [];
        if (typeof res === 'object') docs = res.data.data;

        return docs || [];
      });
    },
    {
      onSettled: () => {
        removeCache(['container']);
        controlBackdropLoading(-1);
      },
    },
  );
};

export const useAddContainerMutation = () => {
  return useMutation(
    ({ driverFirstName, driverLastName, licensePlateNumber, ...props }: any) => {
      if (driverFirstName) props.driver = { ...props.driver, firstName: driverFirstName };
      if (driverLastName) props.driver = { ...props.driver, lastName: driverLastName };
      if (licensePlateNumber)
        props.driver = {
          ...props.driver,
          licensePlateNumber: licensePlateNumber,
        };

      props.containers = props.containerNumber.map((_: string, i: number) => ({
        containerNumber: props.containerNumber[i],
        containerType: props.containerType[i],
        ...(props.condition[i] && { condition: props.condition[i] }),
        isLoaded: props.isLoaded[i],
        completedAt: props.completedAt[i] && toIsoDate(props.completedAt[i]),
      }));
      delete props.containerNumber;
      delete props.containerType;
      delete props.condition;
      delete props.isLoaded;
      delete props.completedAt;

      return Promise.all(
        splitIntoChunks(props.containers).map((chunk: any[]) =>
          containersService.add({ ...props, containers: chunk }),
        ),
      );
    },
    {
      onSettled: () => {
        removeCache(['container', 'gate-in', 'inventory', 'yard', 'key']);
      },
    },
  );
};

export const useGateOutContainerMutation = () => {
  return useMutation(
    ({ driverFirstName, driverLastName, licensePlateNumber, ...props }: any) => {
      props.driver = licensePlateNumber.map((value: string, i: number) => {
        const driver: any = { licensePlateNumber: value };

        if (driverFirstName[i]) {
          driver.firstName = driverFirstName[i];
        }
        if (driverLastName[i]) {
          driver.lastName = driverLastName[i];
        }

        return driver;
      });

      if (props.completedAt) props.completedAt = toIsoDate(props.completedAt);

      return containersService.out(props);
    },
    {
      onSettled: () => {
        removeCache(['container', 'gate-out', 'inventory', 'yard', 'key']);
      },
    },
  );
};

export const useEditContainerMutation = () => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  return useMutation(
    (props: any) => {
      if (!showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_MANAGER'], user))
        delete props.number;

      return containersService.edit(props);
    },
    {
      onSuccess: () => {
        removeCache(['container', 'gate-in', 'gate-out', 'yard', 'key']);
      },
    },
  );
};

export const useRemovePlaceContainerMutation = () => {
  return useMutation(
    (id: string) => {
      return containersService.removePlace(id);
    },
    {
      onSuccess: () => {
        removeCache(['container', 'yard', 'key']);
      },
    },
  );
};

export const useUpdateHoldContainerMutation = () => {
  return useMutation(
    (props: any) => {
      return containersService.updateHold(props);
    },
    {
      onSettled: () => {
        removeCache(['container', 'gate-in', 'gate-out', 'key']);
      },
    },
  );
};

export const useTransferContainerMutation = () => {
  return useMutation(
    (props: any) => {
      return containersService.transfer(props);
    },
    {
      onSettled: () => {
        removeCache(['container', 'gate-in', 'gate-out', 'repair', 'inventory', 'key']);
      },
    },
  );
};

export const useSellContainerMutation = () => {
  return useMutation(
    (props: SaleCreateProps[]) => {
      return saleService.create(props);
    },
    {
      onSettled: () => {
        removeCache(['container', 'gate-in', 'gate-out', 'sale', 'key']);
      },
    },
  );
};

export const useRepairContainerMutation = () => {
  return useMutation(
    (props: RepairCreateProps) => {
      return repairService.create(props);
    },
    {
      onSettled: () => {
        removeCache(['container', 'repair', 'key']);
      },
    },
  );
};
